import { useEffect, useRef, useState } from "react";
import {
  Plus, Loader2, Save, X, Pencil, Trash2, Eye, Upload, Bold, Italic,
  Heading2, Heading3, List as ListIcon, Link2, Image as ImageIcon,
  Quote, Undo2, Redo2, ExternalLink, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import apiClient, { API_BASE } from "./api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const inputCls =
  "w-full bg-black/50 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-white/40 focus:border-[#FC5106] focus:ring-1 focus:ring-[#FC5106] outline-none transition-all";

function slugify(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "").slice(0, 120);
}

function resolveImg(url) {
  if (!url) return null;
  if (url.startsWith("/api/")) return `${BACKEND}${url}`;
  return url;
}

export default function AdminBlog() {
  const [view, setView] = useState({ mode: "list" }); // or {mode:'edit', post}
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/api/admin/blog");
      setItems(data.items || []);
    } catch { toast.error("Could not load blog posts"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const startEdit = async (post) => {
    try {
      const { data } = await apiClient.get(`/api/admin/blog/${post.id}`);
      setView({ mode: "edit", post: data });
    } catch { toast.error("Could not load post"); }
  };

  const remove = async (p) => {
    if (!confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/api/admin/blog/${p.id}`);
      setItems((arr) => arr.filter((x) => x.id !== p.id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  if (view.mode === "edit") {
    return (
      <BlogEditor
        post={view.post}
        onClose={() => setView({ mode: "list" })}
        onSaved={(saved) => {
          setItems((arr) => {
            const idx = arr.findIndex((x) => x.id === saved.id);
            if (idx === -1) return [saved, ...arr];
            const copy = [...arr]; copy[idx] = { ...copy[idx], ...saved, content: undefined }; return copy;
          });
        }}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-blog">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[#C9BCFF] uppercase tracking-[0.25em] text-xs mb-2">Content</p>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">Blog</h1>
          <p className="text-white/50 text-sm mt-1">{items.length} post{items.length === 1 ? "" : "s"} · drafts and published</p>
        </div>
        <div className="flex gap-2">
          <Link to="/blog" target="_blank" className="btn-secondary !py-2.5 !px-4 !text-sm">
            <ExternalLink className="w-4 h-4" /> View public blog
          </Link>
          <button
            onClick={() => setView({ mode: "edit", post: null })}
            data-testid="blog-new-btn"
            className="btn-primary !py-2.5 !px-4 !text-sm"
          >
            <Plus className="w-4 h-4" /> New post
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#121212] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#FC5106] animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-white/50 text-sm">
            <FileText className="w-10 h-10 mx-auto text-white/20 mb-3" />
            No posts yet. Click "New post" to write your first article.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="blog-list-table">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-white/50 bg-black/40">
                <th className="px-5 py-3 text-left font-medium">Title</th>
                <th className="px-3 py-3 text-left font-medium">Category</th>
                <th className="px-3 py-3 text-left font-medium">Status</th>
                <th className="px-3 py-3 text-left font-medium">Updated</th>
                <th className="px-5 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`blog-row-${p.id}`}>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      {p.featured_image_url && (
                        <img src={resolveImg(p.featured_image_url)} alt="" className="w-14 h-10 object-cover rounded-md border border-white/10" />
                      )}
                      <div>
                        <p className="text-white font-medium">{p.title}</p>
                        <p className="text-white/40 text-xs">/blog/{p.slug}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-white/70">{p.category || "General"}</td>
                  <td className="px-3 py-4">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                      p.status === "published"
                        ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                        : "bg-white/5 text-white/60 border-white/10"
                    }`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-3 py-4 text-white/50 text-xs">{(p.updated_at || "").slice(0, 10)}</td>
                  <td className="px-5 py-4 text-right">
                    <div className="inline-flex items-center gap-1">
                      {p.status === "published" && (
                        <Link to={`/blog/${p.slug}`} target="_blank" className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-white/70" aria-label="View">
                          <Eye className="w-3.5 h-3.5" />
                        </Link>
                      )}
                      <button onClick={() => startEdit(p)} data-testid={`blog-edit-${p.id}`} className="p-2 rounded-full bg-white/5 hover:bg-white/10 text-white/70" aria-label="Edit">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => remove(p)} data-testid={`blog-delete-${p.id}`} className="p-2 rounded-full bg-white/5 hover:bg-red-500/20 hover:text-red-300 text-white/70" aria-label="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function BlogEditor({ post, onClose, onSaved }) {
  const editing = !!post;
  const [form, setForm] = useState({
    title: post?.title || "",
    slug: post?.slug || "",
    excerpt: post?.excerpt || "",
    content: post?.content || "",
    featured_image_url: post?.featured_image_url || "",
    featured_image_alt: post?.featured_image_alt || "",
    meta_title: post?.meta_title || "",
    meta_description: post?.meta_description || "",
    category: post?.category || "General",
    tags: (post?.tags || []).join(", "),
  });
  const [slugTouched, setSlugTouched] = useState(!!post);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const contentRef = useRef(null);
  const featuredFileRef = useRef(null);

  // Keep contenteditable DOM in sync with state on init
  useEffect(() => {
    if (contentRef.current && post?.content && !contentRef.current.innerHTML) {
      contentRef.current.innerHTML = post.content;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onTitleChange = (v) => {
    update("title", v);
    if (!slugTouched) update("slug", slugify(v));
    if (!form.meta_title || form.meta_title === form.title) update("meta_title", v);
  };

  const exec = (cmd, value = null) => {
    contentRef.current?.focus();
    document.execCommand(cmd, false, value);
    syncContent();
  };

  const syncContent = () => {
    if (contentRef.current) update("content", contentRef.current.innerHTML);
  };

  const uploadImage = async (file) => {
    if (!file) return null;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await apiClient.post("/api/admin/blog/images", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
      return null;
    } finally { setUploading(false); }
  };

  const insertImageInContent = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const url = await uploadImage(file);
      if (!url) return;
      const alt = prompt("Alt text (for accessibility & SEO):", "");
      contentRef.current?.focus();
      document.execCommand("insertHTML", false,
        `<img src="${BACKEND}${url}" alt="${(alt || "").replace(/"/g, "&quot;")}" />`);
      syncContent();
    };
    input.click();
  };

  const uploadFeatured = async (file) => {
    const url = await uploadImage(file);
    if (url) update("featured_image_url", url);
  };

  const insertLink = () => {
    const url = prompt("Link URL (https://… or /services):", "https://");
    if (!url) return;
    exec("createLink", url);
  };

  // Sanitize pasted content from Word/Google Docs/web pages — strips dangerous tags + Microsoft junk
  const handlePaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData?.getData("text/html");
    const text = e.clipboardData?.getData("text/plain") || "";
    if (html) {
      // Parse and strip unsafe / Word-specific stuff
      const tmp = document.createElement("div");
      tmp.innerHTML = html;
      tmp.querySelectorAll("script, style, link, meta, iframe, object, embed").forEach((n) => n.remove());
      // Remove Microsoft Office classes & inline styles that often crash rendering
      tmp.querySelectorAll("*").forEach((el) => {
        el.removeAttribute("style");
        el.removeAttribute("class");
        // Remove all event-handler attributes (onclick, onerror, etc.)
        [...el.attributes].forEach((a) => {
          if (a.name.startsWith("on")) el.removeAttribute(a.name);
        });
      });
      // Whitelist: only allow safe tags
      const ok = new Set(["P","BR","H1","H2","H3","H4","UL","OL","LI","STRONG","EM","B","I","A","IMG","BLOCKQUOTE","CODE","PRE","HR","DIV","SPAN"]);
      tmp.querySelectorAll("*").forEach((el) => {
        if (!ok.has(el.tagName)) {
          // Unwrap unknown tags but keep their text content
          while (el.firstChild) el.parentNode.insertBefore(el.firstChild, el);
          el.remove();
        }
      });
      document.execCommand("insertHTML", false, tmp.innerHTML);
    } else {
      document.execCommand("insertText", false, text);
    }
    syncContent();
  };

  const save = async (statusOverride = null) => {
    // Force-sync any pending content from the contenteditable before saving
    const liveContent = contentRef.current ? contentRef.current.innerHTML : form.content;
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        slug: form.slug || undefined,
        excerpt: form.excerpt || undefined,
        content: liveContent,
        featured_image_url: form.featured_image_url || null,
        featured_image_alt: form.featured_image_alt || undefined,
        meta_title: form.meta_title || undefined,
        meta_description: form.meta_description || undefined,
        category: form.category || "General",
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (statusOverride) payload.status = statusOverride;
      let data;
      try {
        if (editing) {
          ({ data } = await apiClient.patch(`/api/admin/blog/${post.id}`, payload));
        } else {
          ({ data } = await apiClient.post(`/api/admin/blog`, { ...payload, status: statusOverride || "draft" }));
        }
      } catch (apiErr) {
        const detail = apiErr?.response?.data?.detail;
        const msg = typeof detail === "string" ? detail : (Array.isArray(detail) ? detail.map(d => d.msg).join(", ") : "Save failed");
        toast.error(msg);
        setSaving(false);
        return;
      }
      toast.success(
        data?.status === "published"
          ? "Published"
          : editing ? "Saved" : "Draft created"
      );
      try {
        onSaved && onSaved(data);
      } catch (cbErr) {
        // Defensive — don't let a parent state update crash the editor
        console.error("onSaved callback error:", cbErr);
      }
      if (!editing) onClose();
    } catch (err) {
      console.error("Unexpected save error:", err);
      toast.error("Something went wrong — your work is still in the editor. Try again.");
    } finally { setSaving(false); }
  };

  const fullUrl = form.slug ? `${window.location.origin}/blog/${form.slug}` : "";

  return (
    <div className="space-y-5" data-testid="blog-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <button onClick={onClose} className="text-white/60 hover:text-white text-sm inline-flex items-center gap-1">
            <X className="w-4 h-4" /> Cancel
          </button>
          <h1 className="font-heading text-2xl font-bold text-white mt-1">
            {editing ? "Edit post" : "New post"}
          </h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => save("draft")}
            disabled={saving}
            data-testid="blog-save-draft"
            className="btn-secondary !py-2.5 !px-4 !text-sm disabled:opacity-50"
          >
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving</> : <><Save className="w-4 h-4" /> Save draft</>}
          </button>
          <button
            onClick={() => save("published")}
            disabled={saving}
            data-testid="blog-publish"
            className="btn-primary !py-2.5 !px-4 !text-sm disabled:opacity-50"
          >
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving</> : <><Eye className="w-4 h-4" /> Publish</>}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-2xl border border-white/10 bg-[#121212] p-5 space-y-3">
            <label className="text-xs uppercase tracking-wider text-white/50 block">Title *</label>
            <input
              data-testid="blog-title"
              value={form.title}
              onChange={(e) => onTitleChange(e.target.value)}
              className={`${inputCls} !text-lg !font-semibold`}
              placeholder="Dog grooming tips every Bhubaneswar owner should know"
            />
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Slug (URL)</label>
                <input
                  data-testid="blog-slug"
                  value={form.slug}
                  onChange={(e) => { setSlugTouched(true); update("slug", slugify(e.target.value)); }}
                  className={inputCls}
                  placeholder="dog-grooming-tips-bhubaneswar"
                />
                {fullUrl && <p className="text-[11px] text-white/40 mt-1 truncate">{fullUrl}</p>}
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Category</label>
                <input
                  data-testid="blog-category"
                  value={form.category}
                  onChange={(e) => update("category", e.target.value)}
                  className={inputCls}
                  placeholder="Dog Grooming"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Tags (comma-separated)</label>
              <input
                data-testid="blog-tags"
                value={form.tags}
                onChange={(e) => update("tags", e.target.value)}
                className={inputCls}
                placeholder="grooming, dog care, bhubaneswar"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">Excerpt (150–200 chars)</label>
              <textarea
                data-testid="blog-excerpt"
                value={form.excerpt}
                onChange={(e) => update("excerpt", e.target.value)}
                rows={2}
                className={`${inputCls} resize-none`}
                placeholder="Short summary shown on blog grid and social previews. Leave blank to auto-generate from content."
                maxLength={300}
              />
              <p className="text-[11px] text-white/40 mt-1">{form.excerpt.length} / 300</p>
            </div>
          </div>

          {/* Editor */}
          <div className="rounded-2xl border border-white/10 bg-[#121212] overflow-hidden">
            <div className="flex flex-wrap items-center gap-1 px-3 py-2 border-b border-white/10 bg-black/30" data-testid="editor-toolbar">
              <ToolBtn onClick={() => exec("bold")} icon={Bold} label="Bold" />
              <ToolBtn onClick={() => exec("italic")} icon={Italic} label="Italic" />
              <div className="w-px h-5 bg-white/10 mx-1" />
              <ToolBtn onClick={() => exec("formatBlock", "H2")} icon={Heading2} label="H2" />
              <ToolBtn onClick={() => exec("formatBlock", "H3")} icon={Heading3} label="H3" />
              <ToolBtn onClick={() => exec("formatBlock", "P")} label="P" text />
              <div className="w-px h-5 bg-white/10 mx-1" />
              <ToolBtn onClick={() => exec("insertUnorderedList")} icon={ListIcon} label="Bullet" />
              <ToolBtn onClick={() => exec("insertOrderedList")} label="1." text />
              <ToolBtn onClick={() => exec("formatBlock", "BLOCKQUOTE")} icon={Quote} label="Quote" />
              <div className="w-px h-5 bg-white/10 mx-1" />
              <ToolBtn onClick={insertLink} icon={Link2} label="Link" />
              <ToolBtn onClick={insertImageInContent} icon={ImageIcon} label="Image" disabled={uploading} />
              <div className="w-px h-5 bg-white/10 mx-1" />
              <ToolBtn onClick={() => exec("undo")} icon={Undo2} label="Undo" />
              <ToolBtn onClick={() => exec("redo")} icon={Redo2} label="Redo" />
              {uploading && <span className="text-xs text-white/50 ml-2 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Uploading…</span>}
            </div>
            <div
              ref={contentRef}
              contentEditable
              suppressContentEditableWarning
              onInput={syncContent}
              onBlur={syncContent}
              onPaste={handlePaste}
              data-testid="blog-content-editor"
              className="blog-prose min-h-[400px] px-6 py-5 outline-none"
              style={{ color: "rgba(255,255,255,0.85)" }}
            />
          </div>
        </div>

        {/* Side column */}
        <div className="space-y-4">
          <div className="rounded-2xl border border-white/10 bg-[#121212] p-5 space-y-3">
            <p className="text-xs uppercase tracking-wider text-white/50">Featured image</p>
            {form.featured_image_url ? (
              <div className="relative">
                <img
                  src={resolveImg(form.featured_image_url)}
                  alt={form.featured_image_alt || form.title}
                  className="w-full aspect-[16/10] object-cover rounded-xl border border-white/10"
                />
                <button
                  type="button"
                  onClick={() => update("featured_image_url", "")}
                  className="absolute top-2 right-2 p-1.5 rounded-full bg-black/70 border border-white/20 text-white/80 hover:bg-red-500/30"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div
                onClick={() => featuredFileRef.current?.click()}
                className="aspect-[16/10] rounded-xl border border-dashed border-white/15 bg-black/40 flex items-center justify-center text-white/40 text-sm cursor-pointer hover:border-[#FC5106]/40"
              >
                <div className="text-center">
                  <Upload className="w-5 h-5 mx-auto mb-1" />
                  Click to upload (max 4MB)
                </div>
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              ref={featuredFileRef}
              hidden
              data-testid="blog-featured-upload"
              onChange={(e) => e.target.files?.[0] && uploadFeatured(e.target.files[0])}
            />
            <input
              data-testid="blog-featured-url"
              value={form.featured_image_url}
              onChange={(e) => update("featured_image_url", e.target.value)}
              className={inputCls}
              placeholder="…or paste image URL"
            />
            <input
              data-testid="blog-featured-alt"
              value={form.featured_image_alt}
              onChange={(e) => update("featured_image_alt", e.target.value)}
              className={inputCls}
              placeholder="Alt text (important for SEO)"
            />
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#121212] p-5 space-y-3">
            <p className="text-xs uppercase tracking-wider text-white/50">SEO</p>
            <div>
              <label className="text-[11px] text-white/50 mb-1 block">Meta title (≤60 chars ideal)</label>
              <input
                data-testid="blog-meta-title"
                value={form.meta_title}
                onChange={(e) => update("meta_title", e.target.value)}
                className={inputCls}
                placeholder="Dog Grooming Tips Bhubaneswar | BarknFurs"
                maxLength={160}
              />
              <p className="text-[11px] text-white/40 mt-1">{form.meta_title.length} / 160</p>
            </div>
            <div>
              <label className="text-[11px] text-white/50 mb-1 block">Meta description (≤160 chars ideal)</label>
              <textarea
                data-testid="blog-meta-desc"
                value={form.meta_description}
                onChange={(e) => update("meta_description", e.target.value)}
                rows={3}
                className={`${inputCls} resize-none`}
                placeholder="Shown in Google search results"
                maxLength={300}
              />
              <p className="text-[11px] text-white/40 mt-1">{form.meta_description.length} / 300</p>
            </div>
            <div className="rounded-lg bg-black/30 border border-white/5 p-3">
              <p className="text-[11px] text-white/40 mb-1">Google preview</p>
              <p className="text-sm text-[#8ab4f8] truncate">{fullUrl || "your-url"}</p>
              <p className="text-[15px] text-white/90 leading-tight truncate">{form.meta_title || form.title || "Your title"}</p>
              <p className="text-xs text-white/50 line-clamp-2">{form.meta_description || form.excerpt || "Description preview"}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ToolBtn({ onClick, icon: Icon, label, text, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      disabled={disabled}
      className="p-2 rounded-md hover:bg-white/10 text-white/70 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed text-xs font-semibold"
    >
      {Icon ? <Icon className="w-4 h-4" /> : label}
    </button>
  );
}
